import { ArchiveImportService } from '../../../core/services/archive-import-service';
import type { GraphDocument } from '../../../core/entities/graph-document';
import type { GraphRepository } from '../../../core/ports/graph-repository';
import { validateAIAssistConfig, validateGraphAppearanceConfig, validateGraphDocument, validateMediaManifest } from '../../../core/policies/archive/validate-archive';
import type { GraphAIAssistConfig } from '../../../core/entities/ai-assist-config';
import type { GraphAppearanceConfig } from '../../../core/entities/graph-appearance-config';
import type { ArchiveSignature, GraphArchivePackage } from '../../../types/archive';
import { parseJourneyNodesArchive } from '../../../storage/archive/journeynodes-parser';
import { IndexedDbMediaRepository } from '../../../storage/implementations/indexeddb-media-repository';
import { hydrateDeidentificationSettings } from '../../preferences/services/deidentification-settings-store';
import { resetAIAssistWorkspace } from '../../ai-assist/services/ai-workspace-store';
import { saveStoredGraphAppearanceConfig } from '../../graph-view/services/graph-appearance-preferences';
import { useGraphStore } from '../../graph-view/services/graph-store';

export const THREE_KINGDOMS_SAMPLE_ARCHIVE_FILENAME = 'three-kingdoms-demo.journeynodes';
export const CHRISTIAN_BIBLE_SAMPLE_ARCHIVE_FILENAME = 'christian-bible-demo.journeynodes';
export const JIN_YONG_SAMPLE_ARCHIVE_FILENAME = 'jin-yong-classics-demo.journeynodes';
export const JIN_YONG_SAMPLE_ARCHIVE_PATH = `/protected-archives/${JIN_YONG_SAMPLE_ARCHIVE_FILENAME}`;
export const DEFAULT_SAMPLE_ARCHIVE_PATH = JIN_YONG_SAMPLE_ARCHIVE_PATH;

/** Public paths for preview-mode templates that do not require Worker authorization. */
export const THREE_KINGDOMS_PUBLIC_PATH = `/sample-archives/${THREE_KINGDOMS_SAMPLE_ARCHIVE_FILENAME}`;
export const CHRISTIAN_BIBLE_PUBLIC_PATH = `/sample-archives/${CHRISTIAN_BIBLE_SAMPLE_ARCHIVE_FILENAME}`;

export interface LoadedArchiveGraph {
  graph: GraphDocument;
  warnings: string[];
}

export interface PreparedArchiveGraph extends LoadedArchiveGraph {
  pkg: GraphArchivePackage;
  aiAssistConfig: GraphAIAssistConfig;
	graphAppearanceConfig: GraphAppearanceConfig;
	archiveSignature?: ArchiveSignature;
}

export interface ArchiveSignatureVerifier {
	verify(archive: GraphArchivePackage, signature: ArchiveSignature): Promise<boolean>;
}

export interface PrepareGraphArchiveImportOptions {
	requireSignedLargeArchives?: boolean;
	signatureVerifier?: ArchiveSignatureVerifier;
}

export async function loadGraphFromArchiveInput(input: Blob | ArrayBuffer | Uint8Array): Promise<LoadedArchiveGraph> {
  const prepared = await prepareGraphArchiveImport(input);

  return {
    graph: prepared.graph,
    warnings: prepared.warnings,
  };
}

export async function prepareGraphArchiveImport(input: Blob | ArrayBuffer | Uint8Array, options: PrepareGraphArchiveImportOptions = {}): Promise<PreparedArchiveGraph> {
  const pkg = await parseJourneyNodesArchive(input);
  const manifestValidation = validateMediaManifest(pkg.mediaManifest);

  if (!manifestValidation.valid || !manifestValidation.manifest) {
    throw new Error(manifestValidation.errors.join('; ') || 'Invalid media manifest.');
  }

  const graphValidation = validateGraphDocument(pkg.graphJson, manifestValidation.manifest);

  if (!graphValidation.valid || !graphValidation.graph) {
    throw new Error(graphValidation.errors.join('; ') || 'Invalid graph archive.');
  }

	if (options.requireSignedLargeArchives && graphValidation.graph.nodes.length > 10) {
		if (!pkg.archiveSignature || !options.signatureVerifier) {
			throw new Error('A Worker signed archive is required before importing more than 10 nodes.');
		}

		const signatureValid = await options.signatureVerifier.verify(pkg, pkg.archiveSignature);
		if (!signatureValid) {
			throw new Error('The signed archive could not be verified.');
		}
	}

  const aiConfigValidation = validateAIAssistConfig(pkg.configJson, graphValidation.graph ?? pkg.graphJson);
	const graphAppearanceValidation = validateGraphAppearanceConfig(pkg.configJson);

  return {
    pkg: {
      ...pkg,
      graphJson: graphValidation.graph,
      mediaManifest: manifestValidation.manifest,
    },
    graph: graphValidation.graph,
    warnings: [...graphValidation.warnings, ...aiConfigValidation.warnings, ...graphAppearanceValidation.warnings],
    aiAssistConfig: aiConfigValidation.config,
	graphAppearanceConfig: graphAppearanceValidation.config,
	archiveSignature: pkg.archiveSignature,
  };
}

export async function importPreparedArchiveIntoGraphStore(
  prepared: PreparedArchiveGraph,
  options: { replaceConfirmed: boolean }
): Promise<{ imported: boolean; warnings: string[] }> {
  const service = new ArchiveImportService(createGraphStoreRepository(), new IndexedDbMediaRepository());
  const result = await service.importArchive(prepared.pkg, options);
  if (result.imported) {
    hydrateDeidentificationSettings(prepared.graph, prepared.aiAssistConfig, prepared.warnings);
		await saveStoredGraphAppearanceConfig(prepared.graph.graphId, prepared.graphAppearanceConfig);
    resetAIAssistWorkspace();
  }
  return {
    imported: result.imported,
    warnings: [...result.warnings, ...prepared.warnings],
  };
}

function createGraphStoreRepository(): GraphRepository {
  return {
    async load() {
      return useGraphStore.getState().graph;
    },
    async save(graph) {
      useGraphStore.getState().replaceGraph(graph);
    },
    async replace(graph) {
      useGraphStore.getState().replaceGraph(graph);
    },
    async clear() {
      return undefined;
    },
  };
}
